import React, { useState, useEffect, useCallback } from 'react';
import { usePolling } from '@/hooks/usePolling';
import {
  Card, Upload, Button, Typography, Space, message, Progress, Tag, Collapse,
  Row, Col, Spin, Empty, Popconfirm, List, Divider, Alert,
} from 'antd';
import {
  UploadOutlined, FileTextOutlined, DeleteOutlined, CheckCircleOutlined,
  CloseCircleOutlined, BulbOutlined, LoadingOutlined, UserOutlined,
  StarOutlined, WarningOutlined,
} from '@ant-design/icons';
import type { UploadProps } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { resumeScoreApi, type ResumeScoreDto, type RequirementMatch } from '@/api/resumeScoreApi';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

interface Props {
  opportunityId: string;
}

const formatFileSize = (bytes?: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getScoreColor = (score: number): string => {
  if (score >= 80) return '#52c41a';
  if (score >= 60) return '#faad14';
  if (score >= 40) return '#fa8c16';
  return '#ff4d4f';
};

const ResumeScoringSection: React.FC<Props> = ({ opportunityId }) => {
  const [scores, setScores] = useState<ResumeScoreDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const fetchScores = useCallback(async () => {
    try {
      const res = await resumeScoreApi.getAll(opportunityId);
      setScores(res.data);
      return res.data;
    } catch {
      return scores;
    }
  }, [opportunityId]);

  useEffect(() => {
    setLoading(true);
    fetchScores().finally(() => setLoading(false));
  }, [fetchScores]);

  // Poll while any resume is still scoring; pauses when the tab is hidden.
  const hasPending = scores.some((s) => s.status === 'PENDING' || s.status === 'RUNNING');
  usePolling(async () => { await fetchScores(); }, 4000, hasPending);

  const handleUpload: UploadProps['customRequest'] = async (options) => {
    const file = options.file as File;
    setUploading(true);
    try {
      await resumeScoreApi.upload(opportunityId, file);
      message.success(`Uploaded ${file.name} — scoring in progress`);
      options.onSuccess?.(null);
      await fetchScores();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      message.error(errorMessage);
      options.onError?.(new Error(errorMessage));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (scoreId: string) => {
    try {
      await resumeScoreApi.delete(opportunityId, scoreId);
      message.success('Resume score deleted');
      await fetchScores();
    } catch {
      message.error('Failed to delete');
    }
  };

  const categoryLabels: Record<string, { label: string; color: string }> = {
    resource_requirements: { label: 'Resource', color: '#722ed1' },
    administrative_pass_fail: { label: 'Admin', color: '#fa8c16' },
    submission_requirements: { label: 'Submission', color: '#1890ff' },
    additional_requirements: { label: 'Additional', color: '#13c2c2' },
  };

  const renderRequirementScores = (reqScores: RequirementMatch[]) => {
    const grouped: Record<string, RequirementMatch[]> = {};
    reqScores.forEach(r => {
      const cat = r.category || 'other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(r);
    });

    const totalMet = reqScores.filter(r => r.met).length;
    const totalCount = reqScores.length;
    const overallPct = totalCount > 0 ? Math.round((totalMet / totalCount) * 100) : 0;

    return (
      <div style={{ marginTop: 12 }}>
        <div style={{
          background: 'var(--color-primary-light)', borderRadius: 8, padding: '16px 20px', marginBottom: 12,
          border: '1px solid #d6e4ff',
        }}>
          <Row gutter={16} align="middle">
            <Col>
              <Progress
                type="circle"
                percent={overallPct}
                size={64}
                strokeColor={getScoreColor(overallPct)}
                format={(pct) => <span style={{ fontSize: 14, fontWeight: 700 }}>{pct}%</span>}
              />
            </Col>
            <Col flex={1}>
              <Text strong style={{ fontSize: 13 }}>Requirement Compliance</Text>
              <br />
              <Text type="secondary" style={{ fontSize: 12 }}>
                {totalMet} of {totalCount} requirements met
              </Text>
            </Col>
            {Object.entries(grouped).map(([cat, items]) => {
              const met = items.filter(r => r.met).length;
              const pct = items.length > 0 ? Math.round((met / items.length) * 100) : 0;
              const info = categoryLabels[cat] || { label: cat, color: '#595959' };
              return (
                <Col key={cat} style={{ textAlign: 'center' }}>
                  <Progress
                    type="dashboard"
                    percent={pct}
                    size={48}
                    strokeColor={getScoreColor(pct)}
                    format={(p) => <span style={{ fontSize: 10, fontWeight: 700 }}>{p}%</span>}
                  />
                  <div style={{ fontSize: 10, fontWeight: 600, color: info.color }}>{info.label}</div>
                  <div style={{ fontSize: 9, color: 'var(--color-text-tertiary)' }}>{met}/{items.length}</div>
                </Col>
              );
            })}
          </Row>
        </div>

        <Collapse
          size="small"
          defaultActiveKey={Object.keys(grouped).filter(cat =>
            grouped[cat].some(r => !r.met)
          )}
          items={Object.entries(grouped).map(([cat, items]) => {
            const info = categoryLabels[cat] || { label: cat, color: '#595959' };
            const met = items.filter(r => r.met).length;
            return {
              key: cat,
              label: (
                <Space>
                  <span style={{ color: info.color, fontWeight: 600 }}>{info.label} Requirements</span>
                  <Tag color={met === items.length ? 'success' : 'warning'}>{met}/{items.length} met</Tag>
                </Space>
              ),
              children: (
                <div>
                  {items.map((req, idx) => (
                    <div key={idx} style={{
                      padding: '8px 12px', marginBottom: 6, borderRadius: 6,
                      background: req.met ? 'var(--color-success-light)' : 'var(--color-error-light)',
                      border: `1px solid ${req.met ? 'var(--color-success)' : 'var(--color-error)'}`,
                    }}>
                      <Space align="start">
                        {req.met
                          ? <CheckCircleOutlined style={{ color: 'var(--color-success)', marginTop: 3 }} />
                          : <CloseCircleOutlined style={{ color: 'var(--color-error)', marginTop: 3 }} />}
                        <div>
                          <Text style={{ fontSize: 12 }}>{req.requirement}</Text>
                          {req.met && req.evidence && (
                            <div style={{ fontSize: 11, color: 'var(--color-success)', marginTop: 2 }}>
                              {req.evidence}
                            </div>
                          )}
                          {!req.met && req.gap && (
                            <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 2 }}>
                              {req.gap}
                            </div>
                          )}
                          {!req.met && req.recommendation && (
                            <div style={{
                              fontSize: 11, marginTop: 4, padding: '4px 8px',
                              background: 'var(--color-primary-light)', borderRadius: 4, border: '1px solid #91d5ff',
                              color: '#0050b3',
                            }}>
                              <BulbOutlined style={{ marginRight: 4 }} />
                              {req.recommendation}
                            </div>
                          )}
                        </div>
                      </Space>
                    </div>
                  ))}
                </div>
              ),
            };
          })}
        />
      </div>
    );
  };

  const renderScoreCard = (score: ResumeScoreDto) => {
    const isProcessing = score.status === 'PENDING' || score.status === 'RUNNING';
    const isExpanded = expandedId === score.id;

    return (
      <Card
        key={score.id}
        size="small"
        style={{ marginBottom: 12, borderColor: isProcessing ? '#91d5ff' : undefined }}
        title={
          <Space>
            <FileTextOutlined />
            <Text strong style={{ fontSize: 13 }}>{score.fileName}</Text>
            {score.fileSizeBytes && (
              <Text type="secondary" style={{ fontSize: 11 }}>{formatFileSize(score.fileSizeBytes)}</Text>
            )}
            {isProcessing && <Tag icon={<LoadingOutlined spin />} color="processing">Scoring...</Tag>}
            {score.status === 'COMPLETED' && score.overallScore != null && (
              <Tag color={score.overallScore >= 80 ? 'success' : score.overallScore >= 60 ? 'warning' : 'error'}>
                Score: {Math.round(score.overallScore)}/100
              </Tag>
            )}
            {score.status === 'FAILED' && <Tag color="error">Failed</Tag>}
          </Space>
        }
        extra={
          <Space>
            {score.status === 'COMPLETED' && (
              <Button size="small" type="link" onClick={() => setExpandedId(isExpanded ? null : score.id)}>
                {isExpanded ? 'Collapse' : 'Details'}
              </Button>
            )}
            <Popconfirm title="Delete this score?" onConfirm={() => handleDelete(score.id)} okText="Delete" okType="danger">
              <Button size="small" type="text" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        }
      >
        {isProcessing && (
          <div style={{ textAlign: 'center', padding: 20 }}>
            <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
            <div style={{ marginTop: 8 }}>
              <Text type="secondary">AI is analyzing the resume against RFO requirements...</Text>
            </div>
          </div>
        )}

        {score.status === 'FAILED' && (
          <Alert type="error" message="Scoring Failed" description={score.errorMessage} showIcon />
        )}

        {score.status === 'COMPLETED' && (
          <>
            <Row gutter={16} align="middle" style={{ marginBottom: 12 }}>
              <Col>
                <Progress
                  type="circle"
                  percent={Math.round(score.overallScore ?? 0)}
                  size={80}
                  strokeColor={getScoreColor(score.overallScore ?? 0)}
                  format={(pct) => <span style={{ fontSize: 18, fontWeight: 700 }}>{pct}</span>}
                />
              </Col>
              <Col flex={1}>
                {score.summary && (
                  <Text style={{ fontSize: 13, lineHeight: '1.5' }}>{score.summary}</Text>
                )}
                <div style={{ marginTop: 6 }}>
                  {score.triggeredByName && (
                    <Text type="secondary" style={{ fontSize: 11, marginRight: 12 }}>
                      <UserOutlined style={{ marginRight: 3 }} /> {score.triggeredByName}
                    </Text>
                  )}
                  {score.completedAt && (
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      {dayjs(score.completedAt).fromNow()}
                    </Text>
                  )}
                </div>
              </Col>
            </Row>

            {isExpanded && (
              <>
                <Divider style={{ margin: '12px 0' }} />

                <Row gutter={16}>
                  {score.strengths && score.strengths.length > 0 && (
                    <Col xs={24} md={12}>
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: 'var(--color-success)', fontSize: 12 }}>
                          <StarOutlined style={{ marginRight: 4 }} /> Strengths
                        </Text>
                        <List
                          size="small"
                          dataSource={score.strengths}
                          renderItem={(item) => (
                            <List.Item style={{ padding: '4px 0', border: 'none' }}>
                              <Text style={{ fontSize: 12 }}>
                                <CheckCircleOutlined style={{ color: 'var(--color-success)', marginRight: 6 }} />
                                {item}
                              </Text>
                            </List.Item>
                          )}
                        />
                      </div>
                    </Col>
                  )}

                  {score.gaps && score.gaps.length > 0 && (
                    <Col xs={24} md={12}>
                      <div style={{ marginBottom: 12 }}>
                        <Text strong style={{ color: 'var(--color-error)', fontSize: 12 }}>
                          <WarningOutlined style={{ marginRight: 4 }} /> Gaps
                        </Text>
                        <List
                          size="small"
                          dataSource={score.gaps}
                          renderItem={(item) => (
                            <List.Item style={{ padding: '4px 0', border: 'none' }}>
                              <Text style={{ fontSize: 12 }}>
                                <CloseCircleOutlined style={{ color: 'var(--color-error)', marginRight: 6 }} />
                                {item}
                              </Text>
                            </List.Item>
                          )}
                        />
                      </div>
                    </Col>
                  )}
                </Row>

                {score.recommendations && score.recommendations.length > 0 && (
                  <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ color: 'var(--color-primary)', fontSize: 12 }}>
                      <BulbOutlined style={{ marginRight: 4 }} /> Recommendations
                    </Text>
                    <List
                      size="small"
                      dataSource={score.recommendations}
                      renderItem={(item) => (
                        <List.Item style={{ padding: '4px 0', border: 'none' }}>
                          <Text style={{ fontSize: 12 }}>
                            <BulbOutlined style={{ color: 'var(--color-primary)', marginRight: 6 }} />
                            {item}
                          </Text>
                        </List.Item>
                      )}
                    />
                  </div>
                )}

                {score.requirementScores && score.requirementScores.length > 0 &&
                  renderRequirementScores(score.requirementScores)
                }

                {score.modelUsed && (
                  <div style={{ marginTop: 8 }}>
                    <Text type="secondary" style={{ fontSize: 10 }}>
                      Model: {score.modelUsed} | Tokens: {score.tokensUsed?.toLocaleString()}
                    </Text>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </Card>
    );
  };

  return (
    <Card
      title={
        <Space>
          <FileTextOutlined style={{ color: 'var(--color-purple)' }} />
          <span>Resume Scoring (AI)</span>
          {scores.filter(s => s.status === 'COMPLETED').length > 0 && (
            <Tag color="purple">{scores.filter(s => s.status === 'COMPLETED').length} scored</Tag>
          )}
        </Space>
      }
      style={{ marginBottom: 16 }}
    >
      <Upload
        customRequest={handleUpload}
        showUploadList={false}
        accept=".pdf,.docx,.doc,.txt"
        multiple
        disabled={uploading}
      >
        <Button
          icon={<UploadOutlined />}
          loading={uploading}
          type="dashed"
          style={{ width: '100%', marginBottom: 16, height: 60 }}
        >
          {uploading ? 'Uploading...' : 'Upload Resume(s) — PDF, DOCX, DOC, or TXT'}
        </Button>
      </Upload>

      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 16, marginTop: -8 }}>
        Each resume is scored against the RFO requirements. Upload one or more files to compare candidates.
      </Text>

      {loading && scores.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30 }}>
          <Spin />
        </div>
      ) : scores.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No resumes scored yet. Upload a resume to get started."
        />
      ) : (
        scores.map(renderScoreCard)
      )}
    </Card>
  );
};

export default ResumeScoringSection;
